import { CheckOutlined } from "@ant-design/icons";
import { Button, Card, Form, Image, Input, Select, Space } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import CloseIcon from "../../../../assets/icons/CloseIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import UploadIcon from "../../../../assets/icons/UploadIcon";
import SuccessModal from "../../../../components/common/SuccessModal";
import Loader from "../../../../components/ui/Loader";
import {
  useCreateCollectionPointMutation,
  useGetSingleCollectionPointQuery,
  useUpdateCollectionPointMutation,
} from "../../../../features/cashCollection/cashCollectionSetup/cashCollectionSetupApi";
import {
  useGetAllAreaQuery,
  useGetAllRouteQuery,
  useGetAllTerritoryQuery,
  useGetAllZoneQuery,
} from "../../../../features/cashCollection/geoSetup/geoSetupApi";
import { useUploadFileMutation } from "../../../../features/portal/fileUpload/fileUpload";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import ConfirmModalLayout from "../../../../layouts/ConfirmModalLayout";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { capitalize } from "../../../../utils/string";

export default function AddEditCollectionPoint() {
  const backLink = "/admin/cash-collection-point";
  const [current, setCurrent] = useState(0);
  const [attachment, setAttachment] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();

  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<number | null>(
    null
  );
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
  const [isStepValid, setIsStepValid] = useState(false);

  const {
    open: openConfirmModal,
    handleOpen: handleConfirmOpen,
    handleClose: handleConfirmClose,
  } = useModal<any, "CONFIRM">();

  const {
    open: openSuccessModal,
    handleOpen: handleSuccessOpen,
    handleClose: handleSuccessClose,
  } = useModal<undefined, "SUCCESS">();

  const { user } = useAppSelector((state) => state.auth);

  const [
    uploadFile,
    {
      isLoading: uploadFileIsLoading,
      isError: uploadFileIsError,
      error: uploadFileError,
    },
  ] = useUploadFileMutation();
  const [createCollectionPoint, { isError, error, isLoading, isSuccess }] =
    useCreateCollectionPointMutation();
  const [
    updateCollectionPoint,
    {
      isError: updateCollectionPointIsError,
      error: updateCollectionPointError,
      isSuccess: updateCollectionPointIsSuccess,
      isLoading: updateCollectionPointIsLoading,
    },
  ] = useUpdateCollectionPointMutation();

  const {
    isError: getSingleCollectionPointIsError,
    error: getSingleCollectionPointError,
    isLoading: getSingleCollectionPointIsLoading,
    data: getSingleCollectionPointData,
  } = useGetSingleCollectionPointQuery(id, {
    skip: !id,
  });

  // Get Zone
  const {
    isError: getAllZoneIsError,
    error: getAllZoneError,
    data: getAllZoneData,
    isLoading: getAllZoneIsLoading,
  } = useGetAllZoneQuery({
    params: {
      page: 1,
      limit: 1000000000000,
    },
  });

  // Get all Areas
  const {
    isError: getAllAreaIsError,
    error: getAllAreaError,
    data: getAllAreaData,
    isLoading: getAllAreaIsLoading,
  } = useGetAllAreaQuery(
    {
      params: {
        page: 1,
        limit: 1000000000000,
        zoneId: selectedZoneId ? selectedZoneId : undefined,
      },
    },
    {
      skip: !selectedZoneId,
    }
  );

  // Get all Territories
  const {
    isError: getAllTerritoryIsError,
    error: getAllTerritoryError,
    data: getAllTerritoryData,
    isLoading: getAllTerritoryIsLoading,
  } = useGetAllTerritoryQuery(
    {
      params: {
        page: 1,
        limit: 1000000000000,
        areaId: selectedAreaId ? selectedAreaId : undefined,
      },
    },
    {
      skip: !selectedAreaId,
    }
  );

  // Get all Routes
  const {
    isError: getAllRouteIsError,
    error: getAllRouteError,
    data: getAllRouteData,
    isLoading: getAllRouteIsLoading,
  } = useGetAllRouteQuery(
    {
      params: {
        page: 1,
        limit: 1000000000000,
        territoryId: selectedTerritoryId ? selectedTerritoryId : undefined,
      },
    },
    {
      skip: !selectedTerritoryId,
    }
  );

  useError(isError, error, "Create collection point fail");
  useError(uploadFileIsError, uploadFileError, "File upload fail");
  useError(getAllAreaIsError, getAllAreaError, "Get all area fail");
  useError(getAllZoneIsError, getAllZoneError, "Get all zone fail");
  useError(
    getAllTerritoryIsError,
    getAllTerritoryError,
    "Get all territory fail"
  );
  useError(getAllRouteIsError, getAllRouteError, "Get all route fail");
  useError(
    updateCollectionPointIsError,
    updateCollectionPointError,
    "Update collection point fail"
  );
  useError(
    getSingleCollectionPointIsError,
    getSingleCollectionPointError,
    "Get single collection point data fail"
  );

  const next = () => {
    checkStepFilled(); // ensure validation before moving
    if (isStepValid) {
      setCurrent((prev) => prev + 1);
    }
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const onFinish = (values: any) => {
    const newData = {
      ...values,
      organizationNameShortHand: values?.organizationName?.toLowerCase(),
      collectionPointType: "organization",
      companyId: user?.data?.company_id,
      active: true,
    };

    handleConfirmOpen({ type: "CONFIRM", data: newData });
  };

  // Image Upload Functionality
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxFileSize = 30 * 1024 * 1024;

    if (file.size > maxFileSize) {
      toast("error", `"${file.name}" exceeds max file size of 30MB.`);
      e.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);

    try {
      const res: any = await uploadFile(formData).unwrap();
      if (res?.Location) {
        setAttachment(res.Location); // ✅ local preview
        form.setFieldsValue({ organizationPhoto: res.Location }); // ✅ sync to AntD Form
      }
    } catch (err) {
      console.error(`Upload failed for ${file.name}`, err);
    }

    e.target.value = "";
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const getModalTitle = () => {
    if (openConfirmModal?.type === "CONFIRM" && id) {
      return "Collection Point Successfully Update!";
    }
    if (openConfirmModal?.type === "CONFIRM") {
      return "Collection Point Successfully Added!";
    }
    return "";
  };

  const handleOkay = () => {
    handleConfirmClose();
    handleSuccessClose();
    navigate(`/admin/cash-collection-point`);
  };

  const handleSendToApprove = () => {
    const newUpdateData = {
      ...openConfirmModal?.data,
      active: getSingleCollectionPointData?.data?.active,
    };

    if (openConfirmModal?.type === "CONFIRM" && id) {
      updateCollectionPoint({ data: newUpdateData, id: id });
    } else {
      createCollectionPoint(openConfirmModal?.data);
    }
  };

  const checkStepFilled = async () => {
    // Only trigger validation for step 1 (Contact Info)
    if (current === 1) {
      await form.validateFields(["contactNumber"]).catch(() => {});
    }

    const values = form.getFieldsValue(true);
    const errors = form.getFieldsError();

    // check specifically for the Bangladeshi phone number error on contactNumber
    const phoneError = errors.find(
      (field) =>
        field.name &&
        field.name[0] === "contactNumber" &&
        field.errors.includes("Please enter a valid Bangladeshi phone number")
    );
    const hasPhoneError = !!phoneError;

    if (current === 0) {
      const filled =
        values.organizationPhoto &&
        values.organizationName &&
        values.tradeLicenseNumber;
      setIsStepValid(!!filled); // no forced validation for step 0
    } else if (current === 1) {
      const filled = values.contactPerson; // contactNumber is optional
      setIsStepValid(!!filled && !hasPhoneError);
    } else {
      setIsStepValid(true);
    }
  };

  useEffect(() => {
    checkStepFilled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  useEffect(() => {
    if (getSingleCollectionPointData?.data && id) {
      form.setFieldsValue({
        zoneId: getSingleCollectionPointData.data.zoneId ?? null,
        areaId: getSingleCollectionPointData.data.areaId ?? null,
        territoryId: getSingleCollectionPointData.data.territoryId ?? null,
        routeId: getSingleCollectionPointData.data.routeId ?? null,
        organizationPhoto:
          getSingleCollectionPointData.data.organizationPhoto ?? "",
        organizationName:
          getSingleCollectionPointData.data.organizationName ?? "",
        contactPerson: getSingleCollectionPointData.data.contactPerson ?? "",
        contactNumber: getSingleCollectionPointData.data.contactNumber ?? "",
        tradeLicenseNumber:
          getSingleCollectionPointData?.data?.tradeLicenseNumber,
        lat: getSingleCollectionPointData.data.lat ?? "",
        lon: getSingleCollectionPointData.data.lon ?? "",
      });
      setAttachment(getSingleCollectionPointData.data.organizationPhoto ?? "");
      setSelectedZoneId(getSingleCollectionPointData.data.zoneId ?? null);
      setSelectedAreaId(getSingleCollectionPointData.data.areaId ?? null);
      setSelectedTerritoryId(
        getSingleCollectionPointData.data.territoryId ?? null
      );
      setIsStepValid(true);
    }
  }, [
    getSingleCollectionPointData?.data,
    id,
    form,
    setAttachment,
    setSelectedZoneId,
    setSelectedAreaId,
    setSelectedTerritoryId,
  ]);

  useEffect(() => {
    if (isSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (updateCollectionPointIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCollectionPointIsSuccess]);

  const steps = [
    { title: "Organization Info" },
    { title: "Contact Info" },
    { title: "Geo Assignment" },
  ];

  const renderStep = () => {
    return (
      <>
        {/* Step 1 */}
        <div className={current === 0 ? "block" : "hidden"}>
          <Form.Item
            name="organizationPhoto"
            label="Upload Organization Image"
            rules={[{ required: true, message: "Please upload an image" }]}
          >
            <div className="flex items-center justify-start gap-3 ">
              {attachment ? (
                <div className="relative">
                  <Image
                    src={attachment}
                    className="rounded-full object-cover"
                    alt="Uploaded Photo"
                    width={80}
                    height={80}
                  />
                  <span
                    className="absolute -top-1 -right-1 bg-white p-1 rounded-full shadow cursor-pointer"
                    onClick={() => {
                      setAttachment("");
                      form.setFieldsValue({ organizationPhoto: "" });
                    }}
                  >
                    <CloseIcon />{" "}
                  </span>
                </div>
              ) : (
                <div>
                  {/* Hidden native input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    style={{ display: "none" }}
                    onChange={handleImageChange}
                    accept="image/*"
                  />
                  <Button
                    onClick={triggerFileInput}
                    loading={uploadFileIsLoading}
                    icon={<UploadIcon />}
                  >
                    Upload Photo
                  </Button>
                </div>
              )}
            </div>
          </Form.Item>

          <Form.Item
            label="Organization Name"
            name="organizationName"
            rules={[
              {
                required: true,
                message: "Please enter organization name",
              },
            ]}
          >
            <Input placeholder="Organization Name" />
          </Form.Item>

          <Form.Item
            label="Trade License Number"
            name="tradeLicenseNumber"
            rules={[
              {
                required: true,
                message: "Please enter Trade License Number",
              },
            ]}
          >
            <Input placeholder="Trade License Number" />
          </Form.Item>
        </div>

        {/* Step 2 */}
        <div className={current === 1 ? "block" : "hidden"}>
          <Form.Item
            label="Contact Person"
            name="contactPerson"
            rules={[
              {
                required: true,
                message: "Please enter contact person",
              },
            ]}
          >
            <Input placeholder="Contact Person" />
          </Form.Item>

          <Form.Item
            label="Contact Number (Optional)"
            name="contactNumber"
            rules={[
              {
                validator: async (_: any, value: string) => {
                  // skip validation if empty
                  if (!value) {
                    return Promise.resolve();
                  }

                  const phoneRegex = /^(01[3-9])[0-9]{8}$/;
                  if (!phoneRegex.test(value)) {
                    return Promise.reject(
                      new Error("Please enter a valid Bangladeshi phone number")
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input prefix={"+88"} placeholder="Contact Number" />
          </Form.Item>
        </div>

        {/* Step 3 */}
        <div className={current === 2 ? "block" : "hidden"}>
          <Form.Item
            label="Zone"
            name="zoneId"
            rules={[
              {
                required: true,
                message: "Please enter zone",
              },
            ]}
          >
            <Select
              placeholder="Select Zone"
              suffixIcon={<SelectDownIcon />}
              showSearch
              allowClear
              className="w-full"
              options={
                getAllZoneData?.data?.rows?.map((val: any) => ({
                  label: capitalize(val?.zoneName),
                  value: val?.id,
                })) || []
              }
              value={selectedZoneId}
              onChange={(value) => {
                setSelectedZoneId(value ?? null);
                setSelectedAreaId(null);
                setSelectedTerritoryId(null);
                setSelectedRouteId(null);
              }}
            />
          </Form.Item>

          <Form.Item
            label="Area"
            name="areaId"
            rules={[
              {
                required: true,
                message: "Please enter area",
              },
            ]}
          >
            <Select
              placeholder="Select Area"
              suffixIcon={<SelectDownIcon />}
              showSearch
              allowClear
              className="w-full"
              disabled={!selectedZoneId}
              options={
                getAllAreaData?.data?.rows?.map((val: any) => ({
                  label: capitalize(val?.areaName),
                  value: val?.id,
                })) || []
              }
              value={selectedAreaId}
              onChange={(value) => {
                setSelectedAreaId(value ?? null);
                setSelectedTerritoryId(null);
                setSelectedRouteId(null);
              }}
            />
          </Form.Item>

          <Form.Item
            label="Teritory"
            name="territoryId"
            rules={[
              {
                required: true,
                message: "Please enter teritory",
              },
            ]}
          >
            <Select
              placeholder="Select Territory"
              suffixIcon={<SelectDownIcon />}
              showSearch
              allowClear
              className="w-full"
              disabled={!selectedAreaId}
              options={
                getAllTerritoryData?.data?.rows?.map((val: any) => ({
                  label: capitalize(val?.territoryName),
                  value: val?.id,
                })) || []
              }
              value={selectedTerritoryId}
              onChange={(value) => {
                setSelectedTerritoryId(value ?? null);
                setSelectedRouteId(null);
              }}
            />
          </Form.Item>

          <Form.Item
            label="Route"
            name="routeId"
            rules={[
              {
                required: true,
                message: "Please enter route",
              },
            ]}
          >
            <Select
              placeholder="Route"
              suffixIcon={<SelectDownIcon />}
              showSearch
              allowClear
              className="w-full"
              disabled={!selectedTerritoryId}
              options={
                getAllRouteData?.data?.rows?.map((val: any) => ({
                  label: capitalize(val?.routeName),
                  value: val?.id,
                })) || []
              }
              value={selectedRouteId}
              onChange={(value) => setSelectedRouteId(value ?? null)}
            />
          </Form.Item>

          <Form.Item
            label="Lat (Optional)"
            name="lat"
            rules={[
              {
                validator: async (_: any, value: string) => {
                  // skip validation if empty
                  if (!value) return Promise.resolve();

                  const num = Number(value);

                  // Check negative
                  if (num < 0) {
                    return Promise.reject("Lat cannot be negative");
                  }

                  // Check if it's a valid number
                  if (isNaN(num)) {
                    return Promise.reject("Lat must be a valid number");
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Lat" className="w-full" />
          </Form.Item>

          <Form.Item
            label="Long (Optional)"
            name="lon"
            rules={[
              {
                validator: async (_: any, value: string) => {
                  // skip validation if empty
                  if (!value) return Promise.resolve();

                  const num = Number(value);

                  // Check negative
                  if (num < 0) {
                    return Promise.reject("Long cannot be negative");
                  }

                  // Check if it's a valid number
                  if (isNaN(num)) {
                    return Promise.reject("Long must be a valid number");
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Long" className="w-full" />
          </Form.Item>
        </div>
      </>
    );
  };

  return (
    <>
      <SidebarLayout
        title={`${id ? "Update Collection Point" : "Add New Collection Point"}`}
        backLink={backLink}
      >
        {getSingleCollectionPointIsLoading ? (
          <Loader />
        ) : (
          <>
            <div className="w-1/2 mx-auto relative flex justify-between mb-8">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className="flex flex-col items-center relative z-10"
                >
                  {/* Circle */}
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-full border-2
          ${
            index < current
              ? "bg-green-500 border-green-500 text-white"
              : index === current
              ? "bg-orange-500 border-orange-500 text-white"
              : "bg-white border-[#D7DFE9] text-gray-400"
          }`}
                  >
                    {index < current ? <CheckOutlined /> : index + 1}
                  </div>

                  {/* Title */}
                  <p
                    className={`mt-2 text-sm font-medium ${
                      index === current
                        ? "text-[#2D3643]"
                        : index < current
                        ? "text-[#2D3643]"
                        : "text-[#8897AE]"
                    }`}
                  >
                    {step.title}
                  </p>
                </div>
              ))}

              {/* Connector line across steps */}
              <div className="absolute top-5 left-0 right-0 flex justify-between w-[83%] mx-auto">
                {steps.slice(0, -1).map((_, i) => (
                  <div
                    key={i}
                    className="flex-1 border-t border-dashed"
                    style={{ borderColor: "#D7DFE9" }}
                  ></div>
                ))}
              </div>
            </div>

            <Card className="max-w-2xl mx-auto">
              {/* ✅ Form */}
              <Form
                className="custom-form"
                layout="vertical"
                requiredMark="optional"
                size="large"
                onFinish={onFinish}
                form={form}
                onValuesChange={checkStepFilled}
              >
                {renderStep()}

                <Space className="w-full mt-8" size={12}>
                  {current > 0 && (
                    <Button
                      onClick={prev}
                      size="middle"
                      className="normal-dark-button modal-button !h-[40px]"
                    >
                      Back
                    </Button>
                  )}
                  {current < steps.length - 1 && (
                    <Button
                      className="modal-button !h-[40px]"
                      type="primary"
                      size="middle"
                      onClick={next}
                      disabled={!id && !isStepValid}
                    >
                      Next
                    </Button>
                  )}
                  {current === steps.length - 1 && (
                    <Button
                      className="modal-button !h-[40px]"
                      type="primary"
                      size="middle"
                      htmlType="submit"
                    >
                      {id ? "Update" : "Submit"}
                    </Button>
                  )}
                </Space>
              </Form>
            </Card>
          </>
        )}
      </SidebarLayout>

      {openConfirmModal?.type === "CONFIRM" && (
        <ConfirmModalLayout
          title={`${
            id ? "Update Collection Point?" : "Add New Collection Point?"
          }`}
          subTitle={`${
            id
              ? "Are you sure you want to Update collection point?"
              : "Are you sure you want to add new collection point?"
          }`}
          buttonName={`${
            id ? "Update Collection Point" : "Add Collection Point"
          }`}
          handleButton={handleSendToApprove}
          open={openConfirmModal?.open}
          handleClose={handleConfirmClose}
          action
          buttonBgDeep
          loading={isLoading || updateCollectionPointIsLoading}
        ></ConfirmModalLayout>
      )}

      {openSuccessModal.type === "SUCCESS" && (
        <SuccessModal
          open={openSuccessModal?.open}
          handleClose={handleSuccessClose}
          handleOkay={handleOkay}
          data={openSuccessModal?.data}
          title={getModalTitle()}
        />
      )}
    </>
  );
}
